/**
 * The fleet-uniform answers App Store Connect asks for once per app.
 *
 * These are the values `apps/browser-runner/templates/asc-app-info.json`,
 * `asc-age-rating.json`, `asc-pricing.json` and `asc-listing.json` carry, restated as typed
 * constants so the BACKEND can file them over the App Store Connect API. The runner reads those
 * JSON files at runtime from its own working tree; the backend cannot, so the two must be changed
 * together and each template's `$comment` says so. Per-school copy is NOT here — it lives on the
 * `MobileAppStoreListing` row, exactly like the Play listing (`buildStoreListingDefaults`).
 *
 * Everything below is an attestation filed on a school's behalf: the age-rating answers and the
 * content-rights declaration are legal statements, and the reviewer contact reaches a real person.
 * Nothing here is derived, guessed or interpolated — change a value only with the same sign-off the
 * template asked for.
 */

/**
 * `App.contentRightsDeclaration`, filed fleet-wide as "no third-party content".
 *
 * Corrected to this answer on 2026-09-15 (`asc-app-info.json`): the earlier "uses third-party
 * content" was transcribed from the shipped main app and was wrong for the white-label fleet.
 */
export const APPLE_CONTENT_RIGHTS_DECLARATION = 'DOES_NOT_USE_THIRD_PARTY_CONTENT' as const

/** The locale every fleet listing is filed in. Single-locale until a locale-aware UI lands. */
export const APPLE_LISTING_LOCALE = 'en-US'

/**
 * How an approved version reaches the store: by hand.
 *
 * The App Review notes we file promise the reviewer that "the app will be released manually", so
 * an automatic release would contradict what was filed.
 */
export const APPLE_RELEASE_TYPE = 'MANUAL' as const

/**
 * The complete age-rating declaration, as Apple demands it.
 *
 * COMPLETE IS NOT A STYLE CHOICE. Apple rejects a partial PATCH of
 * `ageRatingDeclarations`, naming whichever attribute is missing — learned filing the first one on
 * 2026-09-15 — so there is no field-by-field path and this object must stay exhaustive.
 * `healthOrWellnessTopics` is a BOOLEAN, not one of the frequency enums, for the same reason it is
 * called out in the template: it reads like the others and is not.
 *
 * Signed off 2026-09-15 by the operator and first filed against ASC app 6812212399, which resolved
 * to App Age Rating 4+ — matching the two legacy references (`asc-age-rating.json`).
 */
export const APPLE_AGE_RATING_DECLARATION = {
  // The 2026 question set — answered from what the app actually does.
  parentalControls: false,
  ageAssurance: false,
  unrestrictedWebAccess: false,
  // A closed school community is not a social network, and no part of the app is age-gated at 13.
  // Both answers are "No" — and they must be SENT rather than left out: an unanswered question on
  // App Store Connect's age-rating page blocks submission exactly like a wrong one.
  socialMedia: false,
  socialMediaAgeRestricted: false,
  // TRUE: parents and staff post and send messages; that is user-generated content.
  userGeneratedContent: true,
  // TRUE: the app ships Stream-backed chat and calls.
  messagingAndChat: true,
  advertising: false,
  gambling: false,
  lootBox: false,
  healthOrWellnessTopics: false,

  // The legacy frequency questions, all NONE fleet-wide.
  alcoholTobaccoOrDrugUseOrReferences: 'NONE',
  contests: 'NONE',
  gamblingSimulated: 'NONE',
  gunsOrOtherWeapons: 'NONE',
  medicalOrTreatmentInformation: 'NONE',
  profanityOrCrudeHumor: 'NONE',
  sexualContentGraphicAndNudity: 'NONE',
  sexualContentOrNudity: 'NONE',
  horrorOrFearThemes: 'NONE',
  matureOrSuggestiveThemes: 'NONE',
  violenceCartoonOrFantasy: 'NONE',
  violenceRealisticProlongedGraphicOrSadistic: 'NONE',
  violenceRealistic: 'NONE',

  ageRatingOverrideV2: 'NONE',
  koreaAgeRatingOverride: 'NONE'
} as const

/**
 * Who App Review contacts, and what they are told to sign in with.
 *
 * Not on the listing row and not per school: one person answers for the whole fleet, and a column
 * per school would be seventeen copies of one phone number. The demo account needs no password
 * because the login screen's "Demo login" button signs the reviewer in — `demoAccountRequired` is
 * false for exactly that reason, and Apple then ignores the name and password fields.
 */
export const APPLE_REVIEW_CONTACT = {
  firstName: 'Ruben',
  lastName: 'Perez',
  phone: '+1 818-860-0889',
  email: 'ruben@schooljourney.com',
  demoAccountRequired: false,
  demoAccountName: 'apps@schooljourney.com'
} as const

/**
 * Pricing and availability: free, in the United States and Canada.
 */
export const APPLE_PRICING = {
  free: true,
  /**
   * NOT every territory Apple sells in. The fleet serves North American schools, so a listing
   * available worldwide would put a school's app in front of people who can never use it — and
   * each extra territory is a store page whose content, ratings and legal expectations we would be
   * answering for. Adding one is a decision, which is why this is a list rather than "all".
   */
  availableTerritoryIds: ['USA', 'CAN'],
  /**
   * False, because the list above is deliberate: a territory Apple adds later must be chosen, not
   * inherited.
   */
  availableInNewTerritories: false,
  /** The territory whose price point defines the schedule. Free is free in every currency. */
  baseTerritoryId: 'USA'
} as const
